import type { AdminUser } from '@sbc/domain-models/admin';
import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { MoreHorizontal } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useDeactivateAdminUser } from '@/lib/api/users';
import { ROLE_LABELS } from '@/lib/auth/roles';

function formatDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleDateString() : '—';
}

function UserRowActions({ user }: { user: AdminUser }) {
  const deactivate = useDeactivateAdminUser();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="size-8 px-0" aria-label="Open row actions">
          <MoreHorizontal className="size-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuItem asChild>
          <Link to="/users/$userId/edit" params={{ userId: user.id }}>
            Edit
          </Link>
        </DropdownMenuItem>
        {user.isActive && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              variant="destructive"
              disabled={deactivate.isPending}
              onSelect={() => deactivate.mutate(user.id)}
            >
              Deactivate
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function IntercomTeammateCell({ user }: { user: AdminUser }) {
  if (user.intercomTeammate === null) {
    return <Badge variant="destructive">No Intercom teammate</Badge>;
  }
  return <span>{user.intercomTeammate.name ?? user.intercomTeammate.id}</span>;
}

export const usersColumns: ColumnDef<AdminUser>[] = [
  {
    accessorKey: 'email',
    header: 'Email',
    filterFn: 'includesString',
    cell: ({ row }) => <span className="font-medium">{row.original.email}</span>,
  },
  {
    accessorKey: 'role',
    header: 'Role',
    cell: ({ row }) => <Badge variant="outline">{ROLE_LABELS[row.original.role]}</Badge>,
  },
  {
    id: 'status',
    accessorFn: (user) => (user.isActive ? 'active' : 'inactive'),
    header: 'Status',
    filterFn: 'arrIncludesSome',
    cell: ({ row }) =>
      row.original.isActive ? (
        <Badge variant="secondary">Active</Badge>
      ) : (
        <Badge variant="outline">Inactive</Badge>
      ),
  },
  {
    id: 'intercomTeammate',
    accessorFn: (user) => (user.intercomTeammate ? 'mapped' : 'unmapped'),
    header: 'Intercom teammate',
    filterFn: 'arrIncludesSome',
    cell: ({ row }) => <IntercomTeammateCell user={row.original} />,
  },
  {
    accessorKey: 'lastLoginAt',
    header: 'Last login',
    cell: ({ row }) => formatDate(row.original.lastLoginAt),
  },
  {
    accessorKey: 'createdAt',
    header: 'Created',
    cell: ({ row }) => formatDate(row.original.createdAt),
  },
  {
    id: 'actions',
    enableHiding: false,
    cell: ({ row }) => (
      <div className="text-right">
        <UserRowActions user={row.original} />
      </div>
    ),
  },
];

export const usersStatusFacet = {
  columnId: 'status',
  title: 'Status',
  options: [
    { label: 'Active', value: 'active' },
    { label: 'Inactive', value: 'inactive' },
  ],
};

export const usersIntercomTeammateFacet = {
  columnId: 'intercomTeammate',
  title: 'Intercom',
  options: [
    { label: 'Mapped', value: 'mapped' },
    { label: 'No Intercom teammate', value: 'unmapped' },
  ],
};
